import {compact, naturalSort} from '@benchling/js-utils';
import assert from 'assert';

import {ComplexPolymerConnectionGraphQL} from '../../../../graphql-fragments/modbio/__generated/ComplexPolymerConnectionGraphQL';
import {
  ComplexPolymerConnectionType,
  HELMPolymerType,
  ResolvedAttachmentGraphQL,
  ResolvedAttachmentInput,
} from '../ComplexPolymerConstants';
import {getPolymerTypeFromRef} from '../ComplexPolymerUtil';
import {
  CompleteConjugateConnection,
  findResolvedAttachmentForConnection,
  getComplexPolymerConnectionFromConjugateConnection,
  isCompleteConjugateConnection,
} from './ComplexPolymerAttachConjugateModalUtil';
import {ConjugateConnection, ConjugateConnectionMap} from './ComplexPolymerComponentsSummaryUtil';

type ResolvedAttachmentPolymer = ResolvedAttachmentGraphQL['polymers'][0];

const HELM_POLYMER_TYPE_TO_TYPENAMES: {
  [key in HELMPolymerType]: {
    polymerTypeName: ResolvedAttachmentPolymer['__typename'];
    polymerEntityTypeName: ResolvedAttachmentPolymer['entity']['__typename'];
  };
} = {
  [HELMPolymerType.RNA]: {
    polymerTypeName: 'NucleotideSequencePolymerNode',
    polymerEntityTypeName: 'SequenceNode',
  },
  [HELMPolymerType.PEPTIDE]: {
    polymerTypeName: 'AASequencePolymerNode',
    polymerEntityTypeName: 'ProteinNode',
  },
  [HELMPolymerType.CHEM]: {
    polymerTypeName: 'MoleculePolymerNode',
    polymerEntityTypeName: 'MoleculeNode',
  },
};

function getGraphQLConnectionFromConjugateConnection(
  conjugateConnection: CompleteConjugateConnection
): ComplexPolymerConnectionGraphQL {
  const normalizedConnection = getComplexPolymerConnectionFromConjugateConnection(conjugateConnection);
  return {
    __typename: 'ComplexPolymerConnectionNode',
    connectionType: ComplexPolymerConnectionType.COVALENT_BOND,
    attachmentStyle: normalizedConnection.attachment_style,
    source: {
      __typename: 'ComplexPolymerConnectionSideNode',
      polymerHelmReference: normalizedConnection.source.polymer_ref,
      residueIndex: normalizedConnection.source.residue_index,
      modificationType: normalizedConnection.source.modification_type,
      rGroup: normalizedConnection.source.r_group,
    },
    target: {
      __typename: 'ComplexPolymerConnectionSideNode',
      polymerHelmReference: normalizedConnection.target.polymer_ref,
      residueIndex: normalizedConnection.target.residue_index,
      modificationType: normalizedConnection.target.modification_type,
      rGroup: normalizedConnection.target.r_group,
    },
  };
}

function getPolymersFromConjugateConnection(
  conjugateConnection: ConjugateConnection,
  linkedEntityId?: string | null
): ResolvedAttachmentGraphQL['polymers'] {
  const {helmReference, entityId, linkedEntityHelmReference} = conjugateConnection;
  const {polymerTypeName, polymerEntityTypeName} =
    HELM_POLYMER_TYPE_TO_TYPENAMES[getPolymerTypeFromRef(helmReference)];

  const firstPolymer = {
    __typename: polymerTypeName,
    id: `${helmReference}${entityId}`,
    helmReference,
    entity: {
      __typename: polymerEntityTypeName,
      id: entityId!,
    },
  };

  return linkedEntityHelmReference == null
    ? [firstPolymer]
    : [
        firstPolymer,
        {
          __typename:
            HELM_POLYMER_TYPE_TO_TYPENAMES[getPolymerTypeFromRef(linkedEntityHelmReference)].polymerTypeName,
          id: `${linkedEntityHelmReference}${entityId}`,
          helmReference: linkedEntityHelmReference,
          entity: {
            __typename:
              HELM_POLYMER_TYPE_TO_TYPENAMES[getPolymerTypeFromRef(linkedEntityHelmReference)]
                .polymerEntityTypeName,
            id: linkedEntityId!,
          },
        },
      ];
}

function getGraphQLPolymersFromConjugateConnections(
  relatedConnections: Array<ConjugateConnection>,
  conjugateConnectionMap: ConjugateConnectionMap
): ResolvedAttachmentGraphQL['polymers'] {
  return relatedConnections.flatMap((cc) =>
    getPolymersFromConjugateConnection(
      cc,
      cc.linkedEntityHelmReference != null
        ? (conjugateConnectionMap[cc.linkedEntityHelmReference]?.entityId ?? null)
        : null
    )
  );
}
function getResolvedAttachmentFromConjugateConnection(
  conjugateConnection: CompleteConjugateConnection,
  conjugateConnectionMap: ConjugateConnectionMap,
  resolvedAttachment: ResolvedAttachmentInput | null
): ResolvedAttachmentGraphQL {
  const conjugateConnectionsInResolvedAttachment =
    resolvedAttachment == null
      ? [conjugateConnection]
      : resolvedAttachment.connections_involved.map((ci) => conjugateConnectionMap[ci.target.polymer_ref]);
  const entityNameById = Object.fromEntries(
    Object.values(conjugateConnectionMap).flatMap((conjugateConnection) =>
      conjugateConnection.entityId != null
        ? [[conjugateConnection.entityId, conjugateConnection.entityName]]
        : []
    )
  );

  const polymers = getGraphQLPolymersFromConjugateConnections(
    conjugateConnectionsInResolvedAttachment,
    conjugateConnectionMap
  );
  const connectionsInvolved = conjugateConnectionsInResolvedAttachment.flatMap((cc) =>
    isCompleteConjugateConnection(cc) ? [getGraphQLConnectionFromConjugateConnection(cc)] : []
  );

  return {
    __typename: 'ResolvedAttachmentNode',
    id: naturalSort(polymers.map((polymer) => polymer.id)).join(''),
    chemicalStructureImage: null,
    connectionsInvolved,
    displayName: compact(
      polymers.map((polymer) => {
        const {entity} = polymer;
        assert(!(entity.__typename === 'InaccessibleInfoNode'), 'Expected polymer to be accessible');
        return entityNameById[entity.id];
      })
    ).join(' | '),
    molfile: conjugateConnection.attachmentMolfile!,
    polymers,
  };
}

export default function getResolvedAttachmentsFromConjugateConnections(
  conjugateConnectionMap: ConjugateConnectionMap,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>
): Array<ResolvedAttachmentGraphQL> {
  const resolvedAttachments = Object.values(conjugateConnectionMap).flatMap((conjugateConnection) => {
    if (!isCompleteConjugateConnection(conjugateConnection)) {
      return [];
    }

    const maybeResolvedAttachment = findResolvedAttachmentForConnection(
      getComplexPolymerConnectionFromConjugateConnection(conjugateConnection),
      resolvedAttachmentInputs
    );

    return [
      getResolvedAttachmentFromConjugateConnection(
        conjugateConnection,
        conjugateConnectionMap,
        maybeResolvedAttachment
      ),
    ];
  });

  const dedupedAttachments = Object.fromEntries(resolvedAttachments.map((dc) => [dc.id, dc]));
  return Object.values(dedupedAttachments);
}
